'use client';

import React, { SetStateAction, useState } from 'react';

import { Avatar, Box, Flex, useDisclosure } from '@chakra-ui/react';

import { UserType } from '@enums/index';
import { useAppDispatch } from '@hooks/store';
import useAuthSelector from '@hooks/useAuthSelector';
import useDeleteProfilePic from '@modules/profile/hooks/useDeleteProfilePic';
import { setDeleteProfilePicture } from '@modules/profile/slice';

type CustomAvatarProps = {
    file: File | null;
    setIsImage: React.Dispatch<SetStateAction<string | undefined | null>>;
    setFile: React.Dispatch<SetStateAction<File | null>>;
    imageUrl: string;
};

const CustomAvatar = ({ file, setIsImage, setFile, imageUrl }: CustomAvatarProps) => {
    const [fileSrc, setFileSrc] = useState<string>();
    const { user } = useAuthSelector();
    const { onClose } = useDisclosure();
    const dispatch = useAppDispatch();

    // api
    const { isSuccess } = useDeleteProfilePic({
        userType: user?.userType as UserType,
    });

    React.useEffect(() => {
        if (file) {
            const reader = new FileReader();
            reader.onload = () => {
                if (typeof reader.result === 'string') {
                    setFileSrc(reader.result);
                }
            };
            reader.readAsDataURL(file);
        }
    }, [file]);

    React.useEffect(() => {
        if (imageUrl) {
            setFileSrc(imageUrl);
        }
    }, [imageUrl]);

    React.useEffect(() => {
        if (isSuccess) {
            onClose();
            dispatch(
                setDeleteProfilePicture({
                    isDeleted: true,
                })
            );
            setIsImage(null);
        }
    }, [dispatch, isSuccess, onClose, setIsImage]);

    return (
        <Flex width="100%" alignItems="center" justifyContent="center">
            <label htmlFor="avatar-upload">
                <Box position="relative" role="group" cursor="pointer">
                    <Avatar src={fileSrc} size="xl" loading="lazy" name={user?.name} showBorder />

                    <Box
                        position="absolute"
                        top="0"
                        left="0"
                        right="0"
                        bottom="0"
                        borderRadius="full"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        bg="transparent"
                        _groupHover={{ bg: 'blackAlpha.600' }}
                    >
                        <Box
                            color="white"
                            fontSize="sm"
                            fontWeight="medium"
                            textAlign="center"
                            px={2}
                            opacity={0}
                            _groupHover={{ opacity: 1 }}
                            transition="opacity 0.2s ease"
                        >
                            Change Picture
                        </Box>
                    </Box>

                    <input
                        id="avatar-upload"
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={e => {
                            const selectedFile = e.target.files?.[0];
                            if (selectedFile) {
                                setFile(selectedFile);
                            }
                        }}
                    />
                </Box>
            </label>
        </Flex>
    );
};

export default CustomAvatar;
